import * as React from 'react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconS24CrossCircle24Filled } from '@is24/cosma-ui-icons'

import RemainingCharacters from '../remainingCharacters/RemainingCharacters'
import { ContentStateProperty } from '../../../../@types/types'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { InputLabel } from '../../display/labelText/LabelText'

export type TextAreaProps = {
  id: string
  name: ContentStateProperty
  rows: number
  label?: string | React.ReactChild
  maxLength?: number
  showMaxLengthLabel?: boolean
  disabled?: boolean
  placeholder: string
  allowResize: boolean
  expandOnFocus?: boolean // the box will expand a little to show the user that they can enter also longer text here
  errors: string[]
  value?: string
  style?: React.CSSProperties
  onChange: (field: ContentStateProperty, value: string | undefined) => void
  onBlur?: (field: ContentStateProperty, value: string | undefined, isUnchanged: boolean) => void
  onFocus?: () => void
}

const TextArea: React.FunctionComponent<TextAreaProps> = (props: TextAreaProps) => {
  const { t } = useTranslation()

  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const [state, setState] = useState<{
    isUnchanged: boolean
    lastValue: string | undefined
    currentValue: string | undefined
  }>({
    isUnchanged: true,
    lastValue: props.value,
    currentValue: props.value,
  })
  const { setTouched, formState } = useFormState()
  React.useEffect(() => setState((prev) => ({ ...prev, currentValue: props.value })), [props.value])

  React.useEffect(() => {
    adjustSize()
  }, [state.currentValue])

  const onLostFocus = ({
    relatedTarget,
    currentTarget: { value: newValue },
  }: React.FocusEvent<HTMLTextAreaElement>) => {
    textAreaRef.current!.rows = props.rows
    setTouched(props.name)
    if (
      state.lastValue !== newValue &&
      !(state.lastValue === undefined && newValue === '') &&
      relatedTarget?.id !== `${props.id}-textarea-container`
    ) {
      props.onBlur && props.onBlur(props.name, newValue, state.isUnchanged)
      setState((prev) => ({ ...prev, lastValue: newValue, isUnchanged: false }))
    }
  }

  const doChange = ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) => {
    props.onChange(props.name, value)
    setState((prev) => ({
      ...prev,
      currentValue: value === '' ? undefined : value,
      lastValue: value === '' ? undefined : prev.lastValue,
      isUnchanged: value === '' ? true : prev.isUnchanged,
    }))
    adjustSize()
  }

  const doOnFocus = () => {
    props.expandOnFocus && state.isUnchanged && expandOnFocus()
    props.onFocus && props.onFocus()
  }

  const adjustSize = () => {
    textAreaRef.current!.style.height = 'auto'
    textAreaRef.current!.style.height = textAreaRef.current!.scrollHeight + 'px'
  }

  const expandOnFocus = () => {
    textAreaRef.current!.style.height = 'auto'
    textAreaRef.current!.rows = Math.round(props.rows + props.rows / 2)
  }

  const showErrors = props.errors.length > 0 && (formState.submitCount > 0 || formState.touched[props.name])
  let style = props.style ? { ...props.style, overflow: 'hidden' } : ({ overflow: 'hidden' } as React.CSSProperties)
  if (!props.allowResize) {
    style = { ...style, resize: 'none', overflow: 'visible' }
  }
  const getSuccessClass = () =>
    state.currentValue !== undefined && state.currentValue !== '' && !showErrors ? ' success-border' : ''

  return (
    <div id={`${props.id}-textarea-container`} className='textarea-container'>
      {props.label && (
        <InputLabel id={props.id} propertyName={props.name} labelNode={props.label} labelClass='textarea-label' />
      )}
      <textarea
        ref={textAreaRef}
        tabIndex={0}
        value={state.currentValue}
        placeholder={props.placeholder}
        className={`textarea ${showErrors ? 'error' : ''} ${getSuccessClass()}`}
        id={props.id}
        maxLength={props.maxLength}
        disabled={props.disabled}
        rows={props.rows}
        style={style}
        onChange={doChange}
        onBlur={onLostFocus}
        {...((props.expandOnFocus || props.onFocus) && { onFocus: doOnFocus })}
      />
      {props.showMaxLengthLabel && (
        <RemainingCharacters
          maxLength={props.maxLength}
          valueLength={props.value ? String(props.value).length : undefined}
        />
      )}
      {showErrors && (
        <div role='alert' className='font-error' aria-labelledby={props.id + '-input-label'}>
          <div className='caption caption--error'>
            <IconS24CrossCircle24Filled className={'caption-icon'} />
            {t(`validation.${props.errors[0]}`)}
          </div>
        </div>
      )}
    </div>
  )
}

export default TextArea
